import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addDays,
} from 'date-fns';
import { enUS, ru, vi } from 'date-fns/locale';
import type { Locale } from 'date-fns';

function dateFnsLocaleFor(lang: string): Locale {
  const b = lang.split('-')[0]?.toLowerCase() ?? 'vi';
  if (b === 'ru') return ru;
  if (b === 'vi') return vi;
  return enUS;
}

function isWeekdaysShort(val: unknown): val is string[] {
  return Array.isArray(val) && val.length === 7 && val.every((x) => typeof x === 'string' && x.length > 0);
}

interface DateRangePickerProps {
  value: { from: Date | null; to: Date | null };
  onChange: (range: { from: Date | null; to: Date | null }) => void;
  onTodayClick: () => void;
  onResetClick?: () => void;
}

export function DateRangePicker({ value, onChange, onTodayClick, onResetClick }: DateRangePickerProps) {
  const { i18n, t } = useTranslation();
  const dfLocale = useMemo(() => dateFnsLocaleFor(i18n.language), [i18n.language]);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selecting, setSelecting] = useState<'from' | 'to'>('from');

  const weekDayLabels = useMemo(() => {
    const raw = t('calendar.weekdaysShort', { returnObjects: true });
    if (isWeekdaysShort(raw)) {
      return raw;
    }
    const refMonday = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) => format(addDays(refMonday, i), 'EEE', { locale: dfLocale }));
  }, [dfLocale, t]);

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const firstDayOfMonth = startOfMonth(currentMonth).getDay();
  const firstDayOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const emptyDays = Array(firstDayOffset).fill(null);

  const handleDateClick = (date: Date) => {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (selecting === 'from') {
      onChange({ from: normalizedDate, to: null });
      setSelecting('to');
    } else {
      if (normalizedDate < (value.from || new Date())) {
        onChange({ from: normalizedDate, to: value.from });
      } else {
        onChange({ from: value.from, to: normalizedDate });
      }
      setSelecting('from');
    }
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const isInRange = (date: Date) => {
    if (!value.from || !value.to) return false;
    return date >= value.from && date <= value.to;
  };

  return (
    <div className="min-w-[18.5rem] max-w-[min(100vw-1rem,22rem)] bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center gap-2 mb-3 sm:mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          aria-label={t('calendar.prevMonth')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="min-w-0 flex-1 text-center text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 px-1">
          {format(currentMonth, 'LLLL yyyy', { locale: dfLocale })}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          aria-label={t('calendar.nextMonth')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1.5">
        {weekDayLabels.map((day, idx) => (
          <div
            key={`wd-${idx}`}
            className="min-w-0 text-center text-[10px] sm:text-[11px] font-semibold text-gray-500 dark:text-gray-400 leading-none py-1 px-0"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {emptyDays.map((_, i) => (
          <div key={`e-${i}`} className="aspect-square min-h-[2rem] max-h-9" />
        ))}
        {days.map((day) => {
          const inMonth = isSameMonth(day, currentMonth);
          const inRange = isInRange(day);
          const isFrom = value.from && isSameDay(day, value.from);
          const isTo = value.to && isSameDay(day, value.to);
          const isSelEnd = isFrom || isTo;
          return (
            <button
              key={day.toString()}
              type="button"
              onClick={() => handleDateClick(day)}
              className={[
                'aspect-square min-h-[2rem] max-h-9 w-full max-w-[2.25rem] mx-auto flex items-center justify-center rounded-lg text-xs sm:text-sm font-medium transition-colors',
                !inMonth ? 'text-gray-300 dark:text-gray-600' : 'text-gray-800 dark:text-gray-100',
                isToday(day) && !isSelEnd && !inRange ? 'ring-1 ring-blue-400/60 dark:ring-blue-500/50' : '',
                inRange && !isSelEnd ? 'bg-blue-400/35 dark:bg-blue-500/30 text-gray-900 dark:text-gray-100' : '',
                isSelEnd ? 'bg-blue-600 text-white shadow-sm' : '',
                !isSelEnd && !inRange ? 'hover:bg-gray-100 dark:hover:bg-gray-700/80' : '',
              ].join(' ')}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      <div className="mt-3 sm:mt-4 flex flex-nowrap justify-between items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onTodayClick}
          className="shrink-0 whitespace-nowrap px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
        >
          {t('calendar.today')}
        </button>
        <button
          type="button"
          onClick={onResetClick || (() => onChange({ from: null, to: null }))}
          className="shrink-0 whitespace-nowrap px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md"
        >
          {t('calendar.reset')}
        </button>
      </div>
    </div>
  );
}
